/* eslint-disable jsx-a11y/label-has-associated-control */
import { DetailedHTMLProps, InputHTMLAttributes, useEffect } from 'react';

import { useRouter } from 'next/router';
import clsx from 'clsx';

import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { useUnfortunateWayToAppendCurrency } from '@portal/state/hooks/useUnfortunateWayToAppendCurrency';

import { AssurancesBaB } from '@portal/shared/components/AssurancesBaB/AssurancesBaB';
import { SanityKeyed } from '@portal/types/studio/studio';
import Select from '@portal/shared/ui/Select';
import { useProductStore } from '@portal/state/store/pdpStore';
import styles from './PriceInput.module.scss';

type CadenceOption = SanityKeyed<{
  _type: 'cadenceOption';
  label?: string;
  sellingPlans?: Array<
    SanityKeyed<{
      _type: 'sellingPlanItem';
      targetStore?: 'dev' | 'qa' | 'aud' | 'nzd' | 'eud' | 'ukd' | 'us' | 'au' | 'nz' | 'eu' | 'uk';
      sellingPlan?: string;
    }>
  >;
  queryParam?: string;
}>;

type CadenceOptions = Array<CadenceOption>;

interface Props extends DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  title: string;
  variantId: string;
  price: string;
  pdpOldPrice?: string;
  assurances?: Array<SanityKeyed<string>>;
  oneTimeTitle?: string;
  oneTimePrice?: string;
  oneTimeAssurances?: Array<SanityKeyed<string>>;
  isOneTime: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  hasCadenceSelector?: boolean;
  cadenceOptions?: CadenceOptions;
  addSuperScript?: boolean;
}

export const PriceInput = ({
  title = '',
  variantId,
  price,
  pdpOldPrice,
  assurances,
  oneTimeTitle,
  oneTimePrice,
  oneTimeAssurances,
  isOneTime,
  onChange,
  checked,
  hasCadenceSelector = false,
  cadenceOptions = [],
  addSuperScript = false
}: Props) => {
  const router = useRouter();
  const { symbolCurrency } = useStoreCurrency();
  const { productCadence, setProductCadence, setIsOtpProduct } = useProductStore();
  const quantity = useProductStore((state) => state.quantity);

  const { conditionallyAppendUSDtoPrice, shouldAppendUSD } = useUnfortunateWayToAppendCurrency();

  const formattedAssurances = isOneTime ? oneTimeAssurances : assurances;
  const radioId = `${variantId}-${isOneTime ? 'otp' : 'sub'}`;

  const isValidQty = Number.isInteger(quantity) && quantity > 0;

  const priceScaled = isValidQty ? (parseFloat(price) * quantity).toString() : price;
  const oldPriceScaled =
    isValidQty && pdpOldPrice ? (parseFloat(pdpOldPrice) * quantity).toString() : pdpOldPrice;
  const oneTimePriceScaled =
    isValidQty && !!oneTimePrice ? (parseFloat(oneTimePrice) * quantity).toString() : oneTimePrice;

  const designatedTitle = title;

  useEffect(() => {
    if (cadenceOptions.length > 0) {
      const frequencyQueryParam = router?.query?.frequency;
      const matchingOption = cadenceOptions?.find(
        (option) => frequencyQueryParam === option?.queryParam
      );

      if (frequencyQueryParam && matchingOption && matchingOption.sellingPlans?.[0]?.sellingPlan) {
        setProductCadence(matchingOption.sellingPlans[0].sellingPlan || '');
      } else if (!productCadence && cadenceOptions[0]?.sellingPlans?.[0]?.sellingPlan) {
        setProductCadence(cadenceOptions[0].sellingPlans[0].sellingPlan || '');
      }
    }
  }, [cadenceOptions, productCadence, setProductCadence, router]);

  const handleChange = (value: string) => {
    setIsOtpProduct(false);
    setProductCadence(value as string);
    const newQuery = { ...router.query, cadence: value };

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    );
  };

  return (
    <label
      htmlFor={radioId}
      className={clsx(styles.card, {
        [styles.cardOneTime]: isOneTime,
        [styles.cardChecked]: checked,
        [styles.cardCheckedOneTime]: checked && isOneTime
      })}>
      <input
        type="radio"
        name={designatedTitle}
        id={radioId}
        value={isOneTime ? 'otp' : 'sub'}
        className={styles.input}
        onChange={onChange}
        checked={checked}
      />
      <div className={clsx(styles.main, isOneTime ? styles.mainOneTime : styles.mainSubscription)}>
        {isOneTime && (
          <div className={clsx(styles.content, styles.contentOneTimeNew)}>
            <div className={styles.priceHeaderRow}>
              <div className={styles.checkmarkWrap}>
                <div className={styles.checkmark} />
              </div>
              <div className={clsx(styles.heading, styles.contentOneTimeNew)}>
                {oneTimeTitle && (
                  <span
                    className={clsx(styles.priceTitle, {
                      [styles.USDEnabledText]: shouldAppendUSD(oneTimePriceScaled)
                    })}>
                    {oneTimeTitle}
                  </span>
                )}
                <div className={styles.priceWrapper}>
                  <span
                    className={clsx(styles.priceValue, {
                      [styles.USDEnabledText]: shouldAppendUSD(oneTimePriceScaled)
                    })}>
                    {symbolCurrency}
                    {conditionallyAppendUSDtoPrice(oneTimePriceScaled)}
                  </span>
                </div>
              </div>
            </div>
            <AssurancesBaB
              isSubscription={false}
              assurances={formattedAssurances || []}
              className={styles.assurancesOtp}
              isPdp
            />
          </div>
        )}

        {!isOneTime && (
          <div className={styles.content}>
            <div className={styles.priceHeaderRow}>
              <div className={styles.checkmarkWrap}>
                <div className={styles.checkmark} />
              </div>
              <div className={clsx(styles.heading, styles.headingNewDesign)}>
                <span
                  className={clsx(styles.priceTitle, {
                    [styles.USDEnabledText]: shouldAppendUSD(priceScaled)
                  })}>
                  {designatedTitle}
                </span>
                <div className={styles.price}>
                  <span
                    className={clsx(styles.priceValue, styles.priceValueNewDesign, {
                      [styles.USDEnabledText]: shouldAppendUSD(priceScaled)
                    })}>
                    {symbolCurrency}
                    {conditionallyAppendUSDtoPrice(priceScaled) as string}
                  </span>
                  {oldPriceScaled && (
                    <span className={styles.priceValueOldContainer}>
                      <span
                        aria-label="original price struck through"
                        className={clsx(styles.priceValue, styles.priceValueOld, {
                          [styles.USDEnabledText]: shouldAppendUSD(priceScaled)
                        })}>
                        {symbolCurrency}
                        {oldPriceScaled}
                      </span>
                      {addSuperScript && <sup className={styles.priceSuper}>†</sup>}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {hasCadenceSelector && cadenceOptions && (
              <div className={styles.cadenceSelector}>
                <label>
                  <Select
                    dense
                    className={styles.cadenceSelect}
                    value={
                      router.query.cadence
                        ? router.query.cadence
                        : productCadence || cadenceOptions[0]?.sellingPlans?.[0]?.sellingPlan || ''
                    }
                    isSearchable={false}
                    options={cadenceOptions.map((option) => ({
                      value: option.sellingPlans?.[0]?.sellingPlan || '',
                      label: option.label || ''
                    }))}
                    onChange={handleChange}
                  />
                </label>
              </div>
            )}
            <div
              className={clsx(styles.assurancesWrapper, { [styles.noBorder]: hasCadenceSelector })}>
              <AssurancesBaB assurances={formattedAssurances || []} className={styles.assurances} />
            </div>
          </div>
        )}
      </div>
    </label>
  );
};
